import { useState, useEffect } from 'react';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { AiOutlinePlus } from 'react-icons/ai';
import { Button } from '@wajeez-web/components/ui';
import CreateNewAdminModal from '../components/CreateNewAdminModal';
import EditUserModal from '../components/EditUserModal';
import AssignNewUserRoleModal from '../components/AssignNewUserRoleModal';
import AssignRoleToUserModal from '../components/AssignRoleToUserModal';
import {
  useRolesSelector,
  useRolesDispatch,
  TeamActions,
} from '@wajeez-web/stores/roles';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { DataTable, Dropdown, CheckBoxType } from '@wajeez-web/components/mui';
import { DataTableWrapper } from '@wajeez-web/components/helpers';
import { SuperAdminSystemPermissions } from '@wajeez-web/core/types';

const WajeezTeamPage = () => {
  const dispatch = useRolesDispatch();

  const [userRoleModalState, setUserRoleModalState] = useState({
    state: false,
    selected: '',
  });
  const [adminModalState, setAdminModalState] = useState(false);
  const [userModalState, setUserModalState] = useState(false);
  const [assigNewUserModal, setAssignNewUserModal] = useState(false);

  const { data, status, details } = useRolesSelector(
    (state) => state.wajeezTeam
  );

  const {
    getData,
    tablePreferances,
    setTablePreferences,
    isTableReferencesLoading,
  } = HandleTableData('WajeezTeam', TeamActions.getTeamDataTableAsync);

  return (
    <div className="h-full">
      {userRoleModalState && (
        <AssignRoleToUserModal
          isOpen={userRoleModalState.state}
          onClose={() => {
            setUserRoleModalState({ state: false, selected: '' });
            dispatch(TeamActions.clearUserRoles());
          }}
          onSubmit={async (data) => {
            if (data) {
              const res = await dispatch(
                TeamActions.updateUserRolesAsync({
                  userId: userRoleModalState.selected,
                  userRoles: data,
                })
              );
              if (res.meta.requestStatus === 'fulfilled') {
                setUserRoleModalState({ state: false, selected: '' });
                getData();
                dispatch(TeamActions.clearUserRoles());
              }
            }
          }}
        />
      )}
      {adminModalState && (
        <CreateNewAdminModal
          isOpen={adminModalState}
          onClose={() => {
            setAdminModalState(false);
          }}
          onSubmit={async (data) => {
            if (data) {
              const res = await dispatch(TeamActions.createAdminAsync(data));
              if (res.meta.requestStatus === 'fulfilled') {
                setAdminModalState(false);
                getData();
              }
            }
          }}
        />
      )}

      {userModalState && (
        <EditUserModal
          isOpen={userModalState}
          onClose={() => {
            setUserModalState(false);
            getData();
          }}
          data={details || undefined}
        />
      )}

      {assigNewUserModal && (
        <AssignNewUserRoleModal
          isOpen={assigNewUserModal}
          onClose={() => {
            setAssignNewUserModal(false);
            dispatch(TeamActions.clearUserRoles());
          }}
          onSubmit={async (data) => {
            if (data) {
              const res = await dispatch(
                TeamActions.updateUserRolesAsync(data)
              );
              if (res.meta.requestStatus === 'fulfilled') {
                setAssignNewUserModal(false);
                getData();
                dispatch(TeamActions.clearUserRoles());
              }
            }
          }}
        />
      )}

      <PageLayoutsContainer
        title="Wajeez Team"
        withoutRightPadding={true}
        endSideComponent={[
          {
            element: (
              <Button
                color="white"
                title="Create New Admin"
                bg="primary"
                Icon={AiOutlinePlus}
                onClick={() => {
                  setAdminModalState(true);
                }}
              />
            ),
          },
          {
            element: (
              <Button
                color="white"
                title="Assign new user"
                bg="primary"
                Icon={AiOutlinePlus}
                onClick={() => {
                  setAssignNewUserModal(true);
                }}
              />
            ),
          },
        ]}
        breadCrumbsLinks={[{ title: 'Wajeez-Team' }]}
      >
        <DataTableWrapper
          data={data as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          isLoading={status === 'loading'}
          rowActions={(row) => {
            return [
              {
                title: 'Assign Role',
                permission: SuperAdminSystemPermissions.team.assignRole,
                type: 'request',
                endpoint: 'assignRole',
                action: () => {
                  dispatch(
                    TeamActions.getUserRolesAsync(row[1] as string)
                  ).then((res) => {
                    setUserRoleModalState({
                      state: true,
                      selected: row[1] as string,
                    });
                  });
                },
              },
              {
                title: 'Edit',
                permission: SuperAdminSystemPermissions.team.editUser,
                type: 'request',
                endpoint: 'Edit',
                action: () => {
                  dispatch(TeamActions.getUserByIdAsync(row[1] as string)).then(
                    (res) => {
                      setUserModalState(true);
                    }
                  );
                },
              },
              {
                title: 'Soft Deletion',
                permission: SuperAdminSystemPermissions.team.softDelete,
                type: 'request',
                endpoint: 'softDeletion',
                action: () => {
                  dispatch(
                    TeamActions.softDeleteUserAsync(row[1] as string)
                  ).then((res) => {
                    if (res.meta.requestStatus === 'fulfilled') {
                      getData();
                    }
                  });
                },
              },
              {
                title: row[16] === 'True' ? 'Unblock' : 'Block',
                endpoint: 'block',
                permission: SuperAdminSystemPermissions.team.block,
                keyname: '16',
                type: 'request',
                action: () => {
                  if (row[16] === 'True') {
                    dispatch(
                      TeamActions.unblockUserAsync(row[1] as string)
                    ).then((res) => {
                      if (res.meta.requestStatus === 'fulfilled') {
                        getData();
                      }
                    });
                  } else {
                    dispatch(TeamActions.blockUserAsync(row[1] as string)).then(
                      (res) => {
                        if (res.meta.requestStatus === 'fulfilled') {
                          getData();
                        }
                      }
                    );
                  }
                },
              },
            ];
          }}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </PageLayoutsContainer>
    </div>
  );
};

export default WajeezTeamPage;
